(() => {
  const REMOVED_COLLECTION_ATTRIBUTES = [
    'Camera handover method',
    'Collection address',
    'Collection contact name',
    'Collection company',
    'Collection address line 1',
    'Collection address line 2',
    'Collection town or city',
    'Collection county or state',
    'Collection postcode or ZIP',
    'Collection phone number',
    'Collection country'
  ];

  const initCartBooking = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const lines = drawer?.querySelector('[data-cart-drawer-lines]');
    const checkoutLink = drawer?.querySelector('[data-cart-booking-checkout]');
    if (!drawer || !lines || !checkoutLink || !drawer.dataset.cartUpdateUrl) return;

    let decorateRequest = 0;
    let decorateFrame = 0;

    const textElement = (tag, className, text) => {
      const element = document.createElement(tag);
      if (className) element.className = className;
      element.textContent = text;
      return element;
    };

    const attributeValue = (cart, attributeName) => {
      const value = cart.attributes?.[attributeName];
      return typeof value === 'string' ? value : '';
    };

    const createField = ({ id, label, attribute, value, type = 'text', required = false, optional = false, autocomplete = '' }) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'cart-booking-field';
      const labelElement = document.createElement('label');
      labelElement.htmlFor = id;
      labelElement.append(label);
      if (optional) labelElement.appendChild(textElement('span', '', 'Optional'));
      const input = document.createElement('input');
      input.id = id;
      input.type = type;
      input.value = value;
      input.dataset.bookingAttribute = attribute;
      input.required = required;
      if (autocomplete) input.autocomplete = autocomplete;
      wrapper.append(labelElement, input);
      return wrapper;
    };

    const createBookingForm = (cart) => {
      const form = document.createElement('form');
      form.className = 'cart-booking cart-booking--order';
      form.dataset.cartBookingOrder = '';

      form.append(
        textElement('div', 'cart-booking-heading', 'Camera details'),
        textElement('p', 'cart-booking-help', 'Complete this once for the whole cart. Every repair service in this order applies to the camera shipment described below.')
      );

      const savedSerial = attributeValue(cart, 'Camera serial number(s)') || attributeValue(cart, 'Camera serial number');
      const serialField = createField({
        id: 'CartCameraSerials',
        label: 'Camera serial number(s)',
        attribute: 'Camera serial number(s)',
        value: savedSerial,
        required: true,
        autocomplete: 'off'
      });
      serialField.classList.add('cart-booking-field--serial');
      form.appendChild(serialField);
      form.appendChild(textElement('p', 'cart-booking-help', 'International orders: enter the serial number from the camera base for shipping and customs paperwork. United Kingdom orders: enter N/A. If two cameras travel together, separate their serial numbers with a comma.'));

      const serialContact = textElement('a', 'cart-booking-contact', 'Cannot provide the serial number? Contact PART 92 ↗');
      const email = drawer.dataset.cartContactEmail || 'repairs@part92.co.uk';
      serialContact.href = `mailto:${email}?subject=${encodeURIComponent('Serial number unavailable - repair booking')}`;
      form.appendChild(serialContact);

      const actions = document.createElement('div');
      actions.className = 'cart-booking-actions';
      const saveButton = textElement('button', 'cart-booking-save', 'Save booking details');
      saveButton.type = 'button';
      saveButton.dataset.saveBooking = '';
      const status = textElement('span', 'cart-booking-status', savedSerial ? 'Saved' : 'Complete before checkout');
      status.dataset.bookingStatus = '';
      actions.append(saveButton, status);
      form.appendChild(actions);

      form.addEventListener('input', () => {
        status.textContent = 'Not yet saved';
        status.classList.remove('is-error', 'is-saved');
      });
      form.addEventListener('change', () => {
        status.textContent = 'Not yet saved';
        status.classList.remove('is-error', 'is-saved');
      });

      return form;
    };

    const decorateCart = async () => {
      if (lines.querySelector('[data-cart-booking-order]') || !lines.querySelector('.cart-drawer-line')) return;
      const requestNumber = ++decorateRequest;

      try {
        const response = await fetch(drawer.dataset.cartStateUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Unable to load booking details');
        const cart = await response.json();
        if (requestNumber !== decorateRequest || !cart.items?.length) return;
        if (!lines.querySelector('[data-cart-booking-order]')) lines.appendChild(createBookingForm(cart));
      } catch (error) {
        // The existing cart remains usable and the customer can retry by reopening it.
      }
    };

    const serializeBooking = (form) => {
      const attributes = {};
      const serial = form.querySelector('[data-booking-attribute="Camera serial number(s)"]');
      attributes['Camera serial number(s)'] = serial?.value.trim() || '';
      attributes['Camera serial number'] = '';
      REMOVED_COLLECTION_ATTRIBUTES.forEach((attributeName) => {
        attributes[attributeName] = '';
      });
      return attributes;
    };

    const saveBooking = async (form, { validate = true, quiet = false } = {}) => {
      const status = form.querySelector('[data-booking-status]');
      const saveButton = form.querySelector('[data-save-booking]');

      if (validate && !form.reportValidity()) {
        if (status) {
          status.textContent = 'Complete the required fields';
          status.classList.add('is-error');
        }
        form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return false;
      }

      if (saveButton) saveButton.disabled = true;
      if (status && !quiet) {
        status.textContent = 'Saving…';
        status.classList.remove('is-error', 'is-saved');
      }

      try {
        const response = await fetch(drawer.dataset.cartUpdateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: serializeBooking(form) })
        });
        if (!response.ok) throw new Error('Unable to save booking details');
        await response.json();
        if (status && !quiet) {
          status.textContent = 'Saved';
          status.classList.add('is-saved');
        }
        return true;
      } catch (error) {
        if (status) {
          status.textContent = 'Could not save — try again';
          status.classList.add('is-error');
        }
        return false;
      } finally {
        if (saveButton) saveButton.disabled = false;
      }
    };

    const scheduleDecoration = () => {
      if (decorateFrame) window.cancelAnimationFrame(decorateFrame);
      decorateFrame = window.requestAnimationFrame(() => {
        decorateFrame = 0;
        decorateCart();
      });
    };
    new MutationObserver(scheduleDecoration).observe(lines, { childList: true });
    scheduleDecoration();

    drawer.addEventListener('click', async (event) => {
      if (!(event.target instanceof Element)) return;
      const saveButton = event.target.closest('[data-save-booking]');
      if (!(saveButton instanceof HTMLButtonElement)) return;
      const form = saveButton.closest('[data-cart-booking-order]');
      if (form instanceof HTMLFormElement) await saveBooking(form);
    });

    checkoutLink.addEventListener('click', async (event) => {
      event.preventDefault();
      if (checkoutLink.getAttribute('aria-disabled') === 'true') return;
      checkoutLink.setAttribute('aria-disabled', 'true');
      checkoutLink.classList.add('is-busy');

      await decorateCart();
      const rows = Array.from(lines.querySelectorAll('.cart-drawer-line'));
      const form = lines.querySelector('[data-cart-booking-order]');
      if (!rows.length || !(form instanceof HTMLFormElement) || !(await saveBooking(form))) {
        checkoutLink.removeAttribute('aria-disabled');
        checkoutLink.classList.remove('is-busy');
        return;
      }
      window.location.assign(checkoutLink.href);
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
      event.preventDefault();

      let bookingReference = form.querySelector('input[name="properties[_Booking reference]"]');
      if (!(bookingReference instanceof HTMLInputElement)) {
        bookingReference = document.createElement('input');
        bookingReference.type = 'hidden';
        bookingReference.name = 'properties[_Booking reference]';
        form.appendChild(bookingReference);
      }
      bookingReference.value = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      const submitButton = form.querySelector('button[type="submit"]');
      const originalLabel = submitButton?.innerHTML;
      const originallyDisabled = submitButton?.disabled;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Adding…';
      }

      try {
        const response = await fetch(drawer.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Unable to add repair');
        const opener = document.querySelector('[data-cart-drawer-open]');
        if (opener instanceof HTMLElement) opener.click();
      } catch (error) {
        HTMLFormElement.prototype.submit.call(form);
      } finally {
        if (submitButton && originalLabel) {
          submitButton.innerHTML = originalLabel;
          submitButton.disabled = Boolean(originallyDisabled);
        }
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartBooking, { once: true });
  } else {
    initCartBooking();
  }
})();
